import React, { useState } from "react";
import styles from "./Canvas.module.scss";
import { Node } from "../../models";
import { addNode, setActiveGameItem, setActiveItem } from "../../store/actions";
import { ACTIVE_COLOR, LINK_COLOR, FADE_COLOR, COLORS } from "../../constants";
import { getFirstSillyName } from "../../utils";

import { useDispatch, useSelector } from "react-redux";
import {
  activeItemSelector,
  answersSelector,
  gameAnswersSelector,
  gameGraphSelector,
  graphSelector,
} from "../../store/selectors";
import NodeDrawing from "./NodeDrawing";
import LinkDrawing from "./LinkDrawing";
import Answers from "./Answers";
import { CanvasProps } from "./types";
import { AnswerType } from "../../types";

const Canvas: React.FC<CanvasProps> = ({ isExpanded = false }) => {
  const activeItem = useSelector(
    !isExpanded ? activeItemSelector : activeItemSelector
  );
  const graph = useSelector(!isExpanded ? graphSelector : gameGraphSelector);
  const answers: Node[] | AnswerType[] = isExpanded
    ? useSelector(gameAnswersSelector)
    : useSelector(answersSelector);

  const [svgNode, setSvgNode] = useState<any>(null);

  const dispatch = useDispatch();

  const renderArrow = (color: string): React.ReactElement => {
    return (
      <marker
        key={color}
        id={`arrow-${color}`}
        markerUnits="strokeWidth"
        markerWidth="10"
        markerHeight="10"
        viewBox="0 0 10 10"
        refX="10"
        refY="3"
        orient="auto"
      >
        <path d="M0,0 L6,3 L0,6 L0,0" style={{ fill: color }} />
      </marker>
    );
  };

  const renderArrowSet = (): React.ReactElement[] => {
    return COLORS.map((color) => renderArrow(color));
  };

  const renderDefs = (): React.ReactElement => {
    return (
      <defs>
        {renderArrow(LINK_COLOR)}
        {renderArrow(FADE_COLOR)}
        {renderArrow(ACTIVE_COLOR)}
        {renderArrowSet()}
      </defs>
    );
  };

  const onMouseDown = (event: any) => {
    if (answers) return;

    if (event.ctrlKey) {
      let name;
      let similar: boolean | Node | undefined = true;
      while (similar) {
        name = getFirstSillyName();
        similar = graph.nodes[name];
      }
      const node = new Node({
        name,
        x: event.clientX - offsetX(),
        y: event.clientY - offsetY(),
        weight: 0,
      });
      dispatch(addNode(node, graph));
    }
    {
      !isExpanded
        ? dispatch(setActiveItem(null))
        : dispatch(setActiveGameItem(null));
    }
  };

  const offsetX = () => {
    return svgNode.getBoundingClientRect().x + getStartFromX();
  };

  const offsetY = () => {
    return svgNode.getBoundingClientRect().y;
  };

  const getStartFromX = (): number => {
    const { minX } = graph;
    return minX <= 10 ? -minX + 15 : 0;
  };

  const getStartFromY = (): number => {
    const { minY } = graph;
    return minY <= 10 ? -minY + 15 : 0;
  };

  const renderGraph = (): React.ReactElement[] => {
    return graph.nodeArray.map((node: any) => (
      <NodeDrawing isExpanded={isExpanded} key={node.name} node={node} />
    ));
  };

  const renderLinks = (): React.ReactElement[] => {
    const linkArray = [...graph.linkArray];
    if (activeItem && activeItem.type === "LINK") {
      linkArray.sort((a, b) => {
        const { start, end } = activeItem.link;
        if (a.start.name === start.name && a.end.name === end.name) {
          return 1;
        }
        if (b.start.name === start.name && b.end.name === end.name) {
          return -1;
        }
        return 0;
      });
    }
    return linkArray.map((link) => (
      <LinkDrawing
        isExpanded={isExpanded}
        key={`${link.start.name}->${link.end.name}`}
        link={link}
      />
    ));
  };

  const renderContent = (): React.ReactElement => {
    if (!isExpanded && answers?.length) {
      return <Answers isExpanded={isExpanded} graph={graph} />;
    }
    return (
      <React.Fragment>
        {renderLinks()}
        {renderGraph()}
      </React.Fragment>
    );
  };

  const { minX, maxX, minY, maxY } = graph;

  const startFromX = minX <= 10 ? -minX + 15 : 0;
  const startFromY = minY <= 10 ? -minY + 15 : 0;
  const endAtX = maxX + getStartFromX() + 15;
  const endAtY = maxY + getStartFromY() + 15;

  return (
    <div className={!isExpanded ? styles.canvas : styles.canvas__expanded}>
      <div className={styles.canvas__container}>
        <svg
          width={endAtX > 1000 ? endAtX : 1000}
          height={endAtY > 1200 ? endAtY : 1200}
          ref={(svgNode): void => {
            setSvgNode(svgNode);
          }}
          onMouseDown={(event): void => {
            onMouseDown(event);
          }}
        >
          {renderDefs()}
          <g transform={`translate(${startFromX},${startFromY})`}>
            {renderContent()}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default Canvas;
